import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  MentorshipDetails,
  useUpdateMentorshipCustomName,
} from "@/lib/api/mentorships";
import { getMenteeDisplayName } from "@/lib/redacted-display";
import { Button } from "@somethings-ui/web";
import { useState } from "react";

interface HubNameModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mentorship: MentorshipDetails;
}

export function HubNameModal({
  open,
  onOpenChange,
  mentorship,
}: HubNameModalProps) {
  const [newName, setNewName] = useState("");
  const { mutateAsync: updateCustomName, isPending: isUpdatingCustomName } =
    useUpdateMentorshipCustomName();

  const menteeDisplayName = getMenteeDisplayName(mentorship.mentee);
  const currentName =
    mentorship.metadata?.customName ||
    `${menteeDisplayName} & ${mentorship.mentor.name ?? ""}`;

  const handleSave = async () => {
    if (newName.trim()) {
      await updateCustomName({
        mentorshipId: mentorship.id,
        customName: newName.trim(),
      });
      setNewName("");
      onOpenChange(false);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setNewName("");
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Name your hub</DialogTitle>
          <DialogDescription className="text-base text-black">
            Your new hub name will instantly update for you and your teen.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-black">Current</label>
            <Input value={currentName} disabled className="bg-gray-50" />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-black">New</label>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Add a new name"
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  newName.trim() &&
                  !isUpdatingCustomName
                ) {
                  handleSave();
                }
              }}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            shape="pill"
            onClick={handleSave}
            disabled={!newName.trim() || isUpdatingCustomName}
            loading={isUpdatingCustomName}
            size="md"
          >
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
